import { useQuery } from '@tanstack/react-query';
import { sitePaymentCopy } from '@evi/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { bankBannerHeadline, bankWindowCopy, bannerLocations, type BankWindowInfo } from '@/lib/bankWindowCopy';
import { payoutSetupCopy } from '@/lib/payoutSetupCopy';
import { payoutLocale } from '@/lib/sitePayouts';
import styles from './BankSetupBanner.module.css';

export interface ReimbursementInfo extends BankWindowInfo {
  applicable: boolean; launch_at: string | null; days_remaining: number | null; inside_window: boolean;
  policy_version: string | null;
  /** Reimbursement Start Date; the separate grace_anchor_at starts the 60 days. */
  start_at?: string | null;
  bank_access_first_granted_at?: string | null;
  deadline?: string | null;
  /** First time the location's bank was seen ready; set means it broke later. */
  first_verified_at?: string | null;
  balances?: Array<{ currency: string; status: string; amount_cents: number; sessions: number }>;
}
export interface BankReadiness {
  locations: Array<ReimbursementInfo & { id: string; name: string; ready: boolean; status: string }>;
  owners: Array<{ key: string; label: string; ready: boolean; status: string; has_account: boolean; setup_kind?: 'personal' | 'charger'; earn_url: string | null }>;
}
export function useBankReadiness() {
  const user = useAuthStore(s => s.user);
  return useQuery({ queryKey: ['bank-readiness', user?._id, user?.template],
    queryFn: async () => (await api.get('me/bank-readiness').json<{ data: BankReadiness }>()).data,
    enabled: !!user && ['building_admin', 'property_manager', 'charger_owner'].includes(user.template ?? ''),
    staleTime: 30_000, refetchInterval: 60_000, refetchOnWindowFocus: 'always' });
}

/** The location's 60-day window line. The copy for every state lives in
 *  bankWindowCopy.ts, where it is unit-tested. */
export function BankWindowText({ info }: { info: BankWindowInfo }) {
  return <span>{bankWindowCopy(info, payoutLocale())}</span>;
}

/** Presentation split from the query so all widths and role states use the same real component. */
export function BankSetupBannerView({ data }: { data: BankReadiness }) {
  const missing = bannerLocations(data.locations);
  const owners = data.owners.filter(o => !o.ready);
  const locale = payoutLocale();
  const text = payoutSetupCopy(locale);
  return <>
    {missing.length > 0 && <aside className={styles.banner} aria-label={text.bannerBuildingLabel}>
      <div className={styles.copy}><p>{bankBannerHeadline(missing, locale)}</p>
        <ul>{missing.map(l => <li key={l.id}><strong>{l.name}:</strong> <BankWindowText info={l} /></li>)}</ul>
      </div>
      <a className={styles.action} href={data.locations.length > 1 ? '/my-site?banks=1' : `/site-settings?site=${encodeURIComponent(missing[0]!.id)}#location-bank`}>{text.bannerConnectBank}</a>
    </aside>}
    {owners.length > 0 && <aside className={styles.banner} aria-label={text.bannerOwnerLabel}>
      <div className={styles.copy}><p>{text.bannerOwnerMessage}</p>
        {owners.some(o => o.status === 'unavailable') && <p>{text.bannerOwnerUnverified}</p>}
      </div><a className={styles.action} href="/owner-payouts">{text.bannerSetUpPayouts}</a>
    </aside>}
  </>;
}

export function BankSetupBanner() {
  const query = useBankReadiness();
  const locale = payoutLocale();
  const text = payoutSetupCopy(locale);
  if (query.isError) return <aside className={styles.banner} aria-label={text.bannerUnavailableLabel}><p className={styles.copy}>{text.bannerCheckFailed}</p><button className={styles.action} onClick={() => void query.refetch()}>{sitePaymentCopy(locale).retry}</button></aside>;
  return query.data ? <BankSetupBannerView data={query.data} /> : null;
}
